import React, { useCallback, useMemo, useState } from 'react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import MemberRoleForm from 'src/components/Organization/MemberRoleForm';
import { filterInMemory } from 'src/crud/filterInMemory';
import { ORG_ROLE_TAGS, tagOptions } from 'src/features/orgRoles';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { OrgMemberRow, fetchOrgMembers, removeOrgMember, updateOrgMemberRole } from 'src/hooks/useOrgMembers';
import { useMemberships } from 'src/hooks/useOrganizations';
import { canDemoteMember, canRemoveMember } from 'src/pages/OrgSettings/lastOwner';
import { showAuthPlanError } from 'src/states/planLimit';
import { QUERIES } from 'src/utils/react-query';

/**
 * Members are org-scoped and live in the auth service, not the domain database, so this
 * resource reaches `authClient` through the plain functions in `useOrgMembers` rather than
 * the generated NestJS client every other CRUD page uses. `@restavo/crud` does not care —
 * its `api` slots are four async functions and nothing about them assumes an origin.
 *
 * `useTenantParams()` supplies {orgSlug, restaurantId}; these endpoints need the
 * organization's id, which is why the resource is built inside a hook that can read it
 * rather than declared at module scope.
 */
export const useOrgMembersPage = () => {
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = active?.id ?? null;

  // The last-owner rules are a property of the WHOLE member set, not of one row, but a row
  // action's predicate only receives its row. Keeping the fetched page in state makes the
  // guard exact without a second query over the same data — and state rather than a ref so
  // a re-render actually reaches the predicates.
  const [members, setMembers] = useState<OrgMemberRow[]>([]);

  const resource = useMemo(
    () =>
      defineResource({
        key: QUERIES.ORG_MEMBERS,
        // better-auth returns the organization's full member list in one response — there is
        // no page/limit on this endpoint — so the page size has to clear any real
        // organization rather than paginate a list the server will not paginate.
        pageSize: 1000,
        // No CSV export: better-auth returns at most 100 members, so an export would truncate
        // silently and look complete.
        exportable: false,
        api: {
          list: async (_scope, query?: ListQuery) => {
            const data = organizationId ? await fetchOrgMembers(organizationId) : [];
            setMembers(data);
            // better-auth takes no filter, so the Role chip is applied here over the whole
            // list; `members` above stays unfiltered, since the last-owner rule needs every row.
            const rows = filterInMemory(data, query?.filter);
            return { data: rows, total: rows.length };
          },
          // Unreachable: the Members page hands its create button to `onCreate` (the invite
          // dialog). A member is not created here — inviting produces an Invitation, which
          // lands on the Invitations page.
          create: async () => {
            throw new Error('Members are not created directly; invite instead.');
          },
          update: (_scope, id: string, body: { role: 'owner' | 'admin' | 'member' }) =>
            updateOrgMemberRole(organizationId!, { memberId: id, role: body.role }).catch((error) => {
              showAuthPlanError(error);
              throw error;
            }),
          remove: (_scope, id: string) => removeOrgMember(organizationId!, id),
        },
        // The whole list is loaded, so an in-memory match sees every row rather than only
        // the current page — the condition the dev-mode truncation warning exists for.
        search: 'client',
        searchFields: ['id'],
        filterable: [
          {
            field: 'role',
            labelKey: 'common.role',
            type: 'enum',
            options: tagOptions(ORG_ROLE_TAGS),
          },
        ],
      }),
    [organizationId]
  );

  const page = useMemo(
    () =>
      definePage(resource, {
        titleKey: 'orgSettings.members.title',
        // Carbon renders this as `<total> <description>` ("27 Members"), so it takes a
        // plural noun rather than the page's sentence-length subtitle.
        descriptionKey: 'orgSettings.members.title',
        createLabelKey: 'orgSettings.members.invite',
        updateLabelKey: 'orgSettings.members.changeRole',
        deleteHeadingKey: 'orgSettings.members.removeTitle',
        columns: [
          {
            key: 'name',
            headerKey: 'common.name',
            alwaysVisible: true,
            render: (row: OrgMemberRow) => row.user?.name ?? '',
          },
          { key: 'email', headerKey: 'common.email', render: (row: OrgMemberRow) => row.user?.email ?? '' },
          { key: 'role', headerKey: 'common.role', display: { kind: 'tag', map: ORG_ROLE_TAGS } },
          { key: 'createdAt', headerKey: 'orgSettings.members.joined', display: { kind: 'date' } },
        ],
        form: MemberRoleForm,
        emptyState: <EmptyState />,
        toPayload: (values: any) => ({ role: values.role }),
      }),
    [resource]
  );

  /**
   * Both row guards exist for the same reason: an organization with no owner cannot be
   * administered at all, so the last owner can be neither demoted nor removed. The
   * hand-written table disabled its inline Select and Remove button; these disable the
   * matching menu items, so the rule stays visible before the click rather than arriving as
   * a server error after it.
   */
  const canUpdateRow = useCallback((row: OrgMemberRow) => canDemoteMember(members, row), [members]);
  const canDeleteRow = useCallback((row: OrgMemberRow) => canRemoveMember(members, row), [members]);

  return { page, organizationId, canUpdateRow, canDeleteRow };
};
